/**
 * LocalSyncDriver — no-op driver for `backend: 'local'`.
 *
 * status() reports reachable with empty diffs; pull/push are no-ops;
 * propose/publish always throw OperationNotPermittedError.
 *
 * @docLink packages/library/concepts#sync-driver
 */

import {
  type AssetRef,
  type Library,
  OperationNotPermittedError,
  type ProposeOpts,
  type ProposeResult,
  type PublishOpts,
  type PublishResult,
  type PullOpts,
  type PullResult,
  type PushOpts,
  type PushResult,
  type SyncStatus,
} from "../user-library.js";
import type { LibrarySyncDriver } from "./driver.js";

export class LocalSyncDriver implements LibrarySyncDriver {
  readonly backend = "local" as const;

  async status(_lib: Library): Promise<SyncStatus> {
    return {
      reachable: true,
      localChanges: { added: [], modified: [], deleted: [] },
      notes: ["local library — no remote sync needed"],
    };
  }

  async pull(_lib: Library, _opts?: PullOpts): Promise<PullResult> {
    return { applied: 0, conflicts: [] };
  }

  async push(_lib: Library, _opts?: PushOpts): Promise<PushResult> {
    return { pushed: 0 };
  }

  async propose(_lib: Library, _opts: ProposeOpts): Promise<ProposeResult> {
    throw new OperationNotPermittedError(
      "propose",
      "local libraries have no remote; convert to git first",
    );
  }

  async publish(_lib: Library, _refs: AssetRef[], _opts: PublishOpts): Promise<PublishResult> {
    throw new OperationNotPermittedError("publish", "local libraries cannot publish");
  }
}
